/* ---------------------------------------------------------------------------
(c) 2023-2024, HID Global Corporation, part of ASSA ABLOY.
All rights reserved.

This program is an un published copyrighted work which is proprietary
to HID Global Corporation. This computer program includes Confidential,
Proprietary Information and is a Trade Secret of HID Global Corporation.
Any use, disclosure, modification and/or reproduction is prohibited
unless authorized in writing by HID Global Corporation.

WARNING:  Unauthorized reproduction of this program as well as
unauthorized preparation of derivative works based upon the
program or distribution of copies by sale, rental, lease or
lending are violations of federal copyright laws and state trade
secret laws, punishable by civil and criminal penalties.
--------------------------------------------------------------------------- */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';

import {
  environment,
  deviceEntity,
  userEntity,
  credentialEntity,
  authenticatorEntity,
  serverUrl
} from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  public loaderSubject = new BehaviorSubject<boolean>(false);
  loaderState = this.loaderSubject.asObservable();

  // public enableApprove = new BehaviorSubject<boolean>(false);
  chars: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  // Use a lookup table to find the index.
  lookup: any = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
  // Contactless card name
  cardName!: string;
  reloadHomeComponent = new BehaviorSubject<boolean>(false);
  private downloadingProgressSub = new Subject();

  constructor(private http: HttpClient) {
    this.initLookUpTable();
  }

  // Generate a random string
  generateRandomString = (length: number, possible: string): string => {
    let text = '';
    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  };

  // Loader service - Show
  showLoader() {
    this.loaderSubject.next(true);
  }

  // Loader service - Hide
  hideLoader() {
    this.loaderSubject.next(false);
  }

  focusOut(elementId: string): void {
    const elem = document.getElementById(elementId);
    (<HTMLButtonElement>elem).blur();
  }

  // Fetch user details
  getUserDetails(): Observable<any> {
    const api = environment.sspAPI.user.CREATE;
    return this.http.get<any>(api.replace('{userEntity}', userEntity.USERINFO));
  }

  // To get User details
  getDevicesList(payload: any): Observable<any> {
    const api = environment.sspAPI.device.CREATE;
    return this.http.post<any>(api.replace('{deviceEntity}', deviceEntity.SEARCH), payload);
  }

  // To get User Authenticators
  getAuthenticatorList(payload: any): Observable<any> {
    const api = environment.sspAPI.authenticator.SEARCH;
    return this.http.post<any>(api, payload);
  }

  // Resynchronize OTP and reset failure count (if required)
  synchronizeDevice(payload: any, deviceID: string): Observable<any> {
    const api = environment.sspAPI.device.COMMON;
    return this.http.post<any>(api.replace('{deviceEntity}', deviceEntity.DEVICE).replace('{id}', deviceID), payload);
  }

  // to change password
  changePassword(payload: any, userID: string): Observable<any> {
    const api = environment.sspAPI.user.COMMON;
    return this.http
      .put<any>(api.replace('{id}', userID).replace('{userEntity}', userEntity.CHANGEPASSWORD), payload)
      .pipe(
        tap(() => {}),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
  }

  // to add otp token
  addOtpToken(payload: any): Observable<any> {
    const api = environment.sspAPI.device.CREATE;
    return this.http.post<any>(api.replace('{deviceEntity}', deviceEntity.SETUP), payload).pipe(
      tap(() => {}),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  // delete Device (HID approve device, otp token device)
  deleteDevice(deviceId: any): Observable<any> {
    const api = environment.sspAPI.device.COMMON;
    return this.http.delete<any>(
      api.replace('{deviceEntity}', deviceEntity.DEVICE).replace('{id}', deviceId as string)
    );
  }

  // Approve Secure Code authenticator
  approveSecureCode(payload: any): Observable<any> {
    const api = environment.sspAPI.user.CREATE;
    return this.http.post<any>(api.replace('{userEntity}', userEntity.APPROVESECURECODE), payload);
  }

  // Unassign / Assign device from the user
  updateDevice(payload: any, deviceId: string): Observable<any> {
    const api = environment.sspAPI.device.COMMON;
    return this.http.put<any>(api.replace('{deviceEntity}', deviceEntity.STATUS).replace('{id}', deviceId), payload);
  }

  // To get the credentials details
  getCredentialDetails(id: string): Observable<any> {
    const api = environment.sspAPI.credential.COMMON;
    return this.http.get<any>(api.replace('{credentialEntity}', credentialEntity.STATUS).replace('{id}', id));
  }

  // To update the credentials details
  updateCredentialDetails(payload: any, id: string): Observable<any> {
    const api = environment.sspAPI.credential.COMMON;
    return this.http.put<any>(api.replace('{credentialEntity}', credentialEntity.STATUS).replace('{id}', id), payload);
  }

  // unlock otp device
  unlockDevice(payload: any, id: string): Observable<any> {
    const api = environment.sspAPI.device.COMMON;
    return this.http.post<any>(api.replace('{deviceEntity}', deviceEntity.UNLOCK).replace('{id}', id), payload);
  }

  // get authenticator policy
  getAuthenticatorPolicy(id: string): Observable<any> {
    const api = environment.sspAPI.authenticator.POLICY.COMMON;
    return this.http.get<any>(api.replace('{authenticatorEntity}', authenticatorEntity.policy).replace('{id}', id));
  }

  // Sorts an array of objects where the sort value is a string.
  // Parameters to pass are the array to sort and the key to sort by.
  sortArray(arrayToSort: any[], sortBy: string): any[] {
    let sortedArray: any[] = [];

    sortedArray = arrayToSort.sort(function (a, b) {
      return a[sortBy].toLowerCase() < b[sortBy].toLowerCase()
        ? -1
        : a[sortBy].toLowerCase() > b[sortBy].toLowerCase()
        ? 1
        : 0;
    });

    return sortedArray;
  }

  //revoke token for the tenant
  revoke(tenantId: string, payload: any): Observable<any> {
    const api = environment.authApi.revoke;
    return this.http.post<any>(api.replace('{tenantId}', tenantId), payload);
  }

  //userinfo for the tenant

  userInfo(tenantId: string): Observable<any> {
    const api = environment.authApi.userinfo;
    return this.http.get<any>(api.replace('{tenantId}', tenantId));
  }

  //par request
  par(): Observable<any> {
    const api = environment.sspAPI.par.COMMON;
    return this.http.get<any>(api);
  }

  //FIDO enrollment
  fidoEnrollment(tenantId: string, payload: any): Observable<any> {
    const api = environment.authApi.enroll + '/fido';
    return this.http.post(api.replace('{tenantId}', tenantId), payload);
  }

  // CARD enrollment
  cardEnrollment(tenantId: string, payload: any): Observable<any> {
    const api = environment.authApi.enroll + '/card';
    return this.http.post(api.replace('{tenantId}', tenantId), payload);
  }

  // Init lookup table
  initLookUpTable(): void {
    for (let i = 0; i < this.chars.length; i++) {
      this.lookup[this.chars.charCodeAt(i)] = i;
    }
  }

  // Used in FIDO enrollment process
  base64urlToBase64(base64url: string): string {
    // Replace non-url compatible chars with base64 standard chars
    const base64Str = base64url.replace(/-/g, '+').replace(/_/g, '/');

    // Pad out with standard base64 required padding characters
    const pad = base64url.length % 4;
    if (pad) {
      if (pad === 1) {
        throw new Error('InvalidLengthError: Input base64url string is the wrong length to determine padding');
      }
      base64url += new Array(5 - pad).join('=');
    }

    return base64Str;
  }

  // Used in FIDO enrollment process
  base64ToBase64url(base64String: string): string {
    return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  encode = (arraybuffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(arraybuffer);
    let i: number;
    const len = bytes.length;
    let base64 = '';

    for (i = 0; i < len; i += 3) {
      base64 += this.chars[bytes[i] >> 2];
      base64 += this.chars[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)];
      base64 += this.chars[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)];
      base64 += this.chars[bytes[i + 2] & 63];
    }

    if (len % 3 === 2) {
      base64 = base64.substring(0, base64.length - 1) + '=';
    } else if (len % 3 === 1) {
      base64 = base64.substring(0, base64.length - 2) + '==';
    }

    return base64;
  };

  decode = (base64: string): ArrayBuffer => {
    let bufferLength = base64.length * 0.75;
    const len = base64.length;
    let i: number;
    let p = 0;
    let encoded1: any;
    let encoded2: any;
    let encoded3: any;
    let encoded4: any;

    if (base64[base64.length - 1] === '=') {
      bufferLength--;
      if (base64[base64.length - 2] === '=') {
        bufferLength--;
      }
    }

    const arraybuffer = new ArrayBuffer(bufferLength),
      bytes = new Uint8Array(arraybuffer);

    for (i = 0; i < len; i += 4) {
      encoded1 = this.lookup[base64.charCodeAt(i)];
      encoded2 = this.lookup[base64.charCodeAt(i + 1)];
      encoded3 = this.lookup[base64.charCodeAt(i + 2)];
      encoded4 = this.lookup[base64.charCodeAt(i + 3)];

      bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
      bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
      bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
    }

    return arraybuffer;
  };

  // Get the card friendly name
  get card(): string {
    return this.cardName;
  }

  // Get the card friendly name
  set card(name: string) {
    this.cardName = name;
  }

  // Update friendly name
  updateFriendlyName(did: string, payload: any): Observable<any> {
    const api = environment.sspAPI.device.COMMON;
    return this.http.put<any>(api.replace('{deviceEntity}', deviceEntity.DEVICE).replace('{id}', did), payload);
  }

  download(url: string): Observable<Blob> {
    return this.http
      .get(url, {
        reportProgress: true,
        observe: 'events',
        responseType: 'blob'
      })
      .pipe(
        tap((response: any) => {
          if (response.type === HttpEventType.DownloadProgress) {
            this.setDownloadingProgress(response.loaded / response.total);
          } else if (response.type === HttpEventType.Response) {
            this.downloadClient(response.body as Blob);
          }
        }),
        catchError((err) => {
          return throwError(() => err);
        })
      );
  }

  getDownloadingProgress(): any {
    return this.downloadingProgressSub.asObservable();
  }

  setDownloadingProgress(data: any): void {
    this.downloadingProgressSub.next(data);
  }

  private downloadClient(blob: Blob): void {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = 'HID Authentication Device Client.msi';
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  getADCUrl(data: string): string {
    let url = '';
    if (data === 'authCardClientURL') {
      url = serverUrl.config?.authCardClientURL + '/x64/HID%20Authentication%20Device%20Client.msi';
    }
    return url;
  }

  // get current adc version
  getcurrentAdcVersion(): Observable<any> {
    const url = 'https://127.0.0.1:52181/manifest.json';
    return this.http.get(url);
  }

  // get minimum supported adc version
  getMinimumAdcVersion(): Observable<any> {
    const url = environment.sspAPI.adc.COMMON.replace('{id}', 'ADC');
    return this.http.get(url);
  }

  // get latest available adc version
  getLatestAdcVersion(): Observable<any> {
    const url = serverUrl.config?.authCardClientURL + '/manifest.json';
    return this.http.get(url);
  }

  /**
   * Retrieves customization data from the common workflow API endpoint.
   *
   * @returns An Observable emitting the customization data retrieved from the API.
   */
  getCustomization(): Observable<any> {
    const url = environment.sspAPI.workflow.COMMON;
    return this.http.get(url);
  }

  /**
   * Retrieves PIN authentication information from the backend API.
   *
   * Sends a GET request to the `/ssp/Application/PINAuth` endpoint with the specified API version.
   *
   * @returns An `Observable<any>` that emits the response from the PIN authentication endpoint.
   */
  getPinAuth(): Observable<any> {
    const url = '/ssp/Application/PINAuth?api-version=10';
    return this.http.get(url);
  }

  /**
   * Creates a new authenticator for the specified user.
   *
   * @param payload - The data to be sent in the request body for creating the authenticator.
   * @param userID - The unique identifier of the user for whom the authenticator is being created.
   * @returns An Observable emitting the response from the authenticator creation API.
   */
  createAuthenticator(payload: any, userID: string): Observable<any> {
    const url = environment.sspAPI.authenticator.POST;
    return this.http.post<any>(url, payload);
  }
}
